import * as PIXI from 'pixi.js';
import debounce from 'lodash/debounce';
import store from '__common/store';
import { getStage } from '__editor/panelsEditor/components/stage/stage';
import { getRenderer } from '__editor/panelsEditor/components/renderer/renderer';
import { getPanelsContainer } from '__editor/panelsEditor/components/panels/utils/panelsContainer';
import shadowPanelModel from '__editor/panelsEditor/models/shadowPanel';
import { tooltipCursor } from '__common/models/cursorTooltip';
import { markStageDirty } from '__editor/panelsEditor/components/stage/stageDirty';
import { resetPreviewModuleCounts } from '__editor/panelsEditor/components/rubberBand/previewModuleCounts';

/**
 * Local Pixi rubber-band preview. During drag we update the outline, shadow
 * sprites, remove-mode visibility, and tooltip on the stage directly instead
 * of dispatching RESIZE / ADD_SHADOW_PANELS on every mousemove (those used to
 * re-render the React PanelsEditor and recreate hundreds of sprites).
 *
 * Redux is updated on mouse-up (deploy) or reset.
 */

type PreviewPoint = { x: number; y: number };
type PreviewShadow = { x: number; y: number; id: string | number };

let previewPoints: PreviewPoint[] = [];
let previewShadows: PreviewShadow[] = [];
let previewPanelsToRemove: number[] = [];
let lastTooltipContent: string | null = null;
let outlineGraphic: any = null;
let panelsShadowContainer: any = null;
let lastHiddenPanelIds: number[] = [];

export function previewShadowId(x: number, y: number) {
  return `${Math.round(x * 100) / 100}_${Math.round(y * 100) / 100}`;
}

export function getPreviewPoints() {
  return previewPoints;
}

export function getPreviewShadowPanels() {
  return previewShadows;
}

export function getPreviewPanelsToRemove() {
  return previewPanelsToRemove;
}

export function isRubberBandPreviewActive() {
  return previewPoints.length > 0;
}

export function setPreviewPoints(points: PreviewPoint[]) {
  previewPoints = points || [];
}

export function setPreviewShadowPanels(panels: PreviewShadow[]) {
  previewShadows = panels || [];
}

export function setPreviewPanelsToRemove(ids: number[]) {
  previewPanelsToRemove = ids || [];
}

export function applyPreviewPanelSelection(mode: string, ids: number[]) {
  if (mode === 'remove') {
    setHiddenPanelsOnStage(ids);
    return;
  }
  setPreviewPanelsToRemove(ids);
}

export function getPanelsShadowContainer() {
  if (panelsShadowContainer) {
    return panelsShadowContainer;
  }
  panelsShadowContainer = new PIXI.Container() as any;
  panelsShadowContainer.id = 'panelsShadowContainer';
  panelsShadowContainer.zIndex = 2;
  panelsShadowContainer.interactiveChildren = false;
  panelsShadowContainer.interactive = false;
  panelsShadowContainer.eventMode = 'none';
  return panelsShadowContainer;
}

export function presentPreviewStage() {
  markStageDirty();
  const renderer = getRenderer();
  const stage = getStage();
  if (renderer && stage) {
    renderer.render(stage);
  }
}

export function updateRubberBandOutline(stage: PIXI.Container, points: PreviewPoint[]) {
  if (!stage) {
    return;
  }

  if (!points || points.length < 3) {
    removeOutline(stage);
    return;
  }

  const settings = store.getState().settings;
  const validPoints = points.filter(point => point && Number.isFinite(point.x) && Number.isFinite(point.y));
  if (validPoints.length < 3) {
    return;
  }

  if (!outlineGraphic || outlineGraphic.destroyed) {
    outlineGraphic = new PIXI.Graphics() as any;
    outlineGraphic.id = 'rubberBand';
    outlineGraphic.zIndex = 9999;
  }

  outlineGraphic.clear();
  outlineGraphic.lineStyle(1, settings.rubberBandColor);

  const preparedPoints = [];
  validPoints.forEach((point) => {
    preparedPoints.push(point.x, point.y);
  });
  preparedPoints.push(validPoints[0].x, validPoints[0].y);

  try {
    outlineGraphic.drawPolygon(preparedPoints);
    outlineGraphic.endFill();
  } catch (_) { /* invalid polygon for this frame */ }

  if (!stage.children.includes(outlineGraphic)) {
    stage.addChild(outlineGraphic);
  }
}

export function diffShadowSprites(existingIds: Array<string | number>, nextPanels: PreviewShadow[]) {
  const nextIds = new Set(nextPanels.map(panel => String(panel.id)));
  const existing = existingIds.map(id => String(id));
  return {
    toRemove: existing.filter(id => !nextIds.has(id)),
    toAdd: nextPanels.filter(panel => !existing.includes(String(panel.id))),
  };
}

export function syncShadowSprites(stage: PIXI.Container, panelsShadow: PreviewShadow[]) {
  if (!stage) {
    return;
  }

  const container = getPanelsShadowContainer();
  const { toRemove, toAdd } = diffShadowSprites(
    container.children.map((child: any) => child.id),
    panelsShadow,
  );
  const removeSet = new Set(toRemove);

  [...container.children].forEach((child: any) => {
    if (removeSet.has(String(child.id))) {
      container.removeChild(child);
      child.destroy();
    }
  });

  toAdd.forEach((panel) => {
    container.addChild(shadowPanelModel(panel));
  });

  if (panelsShadow.length === 0) {
    if (stage.children.includes(container)) {
      stage.removeChild(container);
    }
    return;
  }

  if (!stage.children.includes(container)) {
    stage.addChild(container);
  }
}

const debouncedShadowSync = debounce((stage: PIXI.Container, panelsShadow: PreviewShadow[]) => {
  syncShadowSprites(stage, panelsShadow);
  presentPreviewStage();
}, 16);

export function scheduleShadowSpriteSync(stage: PIXI.Container, panelsShadow: PreviewShadow[]) {
  previewShadows = panelsShadow || [];
  debouncedShadowSync(stage, previewShadows);
}

export function setHiddenPanelsOnStage(ids: number[]) {
  const panelsContainer = getPanelsContainer();
  if (!panelsContainer) {
    previewPanelsToRemove = ids || [];
    return;
  }

  const hide = new Set(ids || []);
  panelsContainer.children.forEach((panel: any) => {
    panel.visible = !hide.has(panel.id);
  });
  lastHiddenPanelIds = ids || [];
  previewPanelsToRemove = lastHiddenPanelIds;
}

export function updatePreviewTooltip(stage: PIXI.Container, content: string | null) {
  if (!stage) {
    return;
  }

  const existingTooltip = stage.children.find((child: any) => child.id === 'cursorTooltip');

  if (!content) {
    if (existingTooltip) {
      stage.removeChild(existingTooltip);
    }
    lastTooltipContent = null;
    return;
  }

  const { editorCursor } = store.getState();
  const x = editorCursor && editorCursor.position ? editorCursor.position.x : 0;
  const y = editorCursor && editorCursor.position ? editorCursor.position.y : 0;

  if (existingTooltip && lastTooltipContent === content) {
    existingTooltip.x = x + 10 / stage.scale.x;
    existingTooltip.y = y + 5 / stage.scale.y;
    return;
  }

  if (existingTooltip) {
    stage.removeChild(existingTooltip);
  }

  const tooltip = tooltipCursor(stage, x, y, content);
  stage.addChild(tooltip);
  lastTooltipContent = content;
}

export function clearRubberBandPreview(stage?: PIXI.Container, panelsContainer?: PIXI.Container) {
  (debouncedShadowSync as any).cancel();

  const targetStage = stage || getStage();
  const targetPanels = panelsContainer || getPanelsContainer();

  if (targetStage) {
    removeOutline(targetStage);
    if (panelsShadowContainer) {
      [...panelsShadowContainer.children].forEach((child: any) => {
        panelsShadowContainer.removeChild(child);
        if (child.destroy) {
          child.destroy();
        }
      });
      if (targetStage.children.includes(panelsShadowContainer)) {
        targetStage.removeChild(panelsShadowContainer);
      }
    }
    const existingTooltip = targetStage.children.find((child: any) => child.id === 'cursorTooltip');
    if (existingTooltip) {
      targetStage.removeChild(existingTooltip);
    }
  }

  if (targetPanels && lastHiddenPanelIds.length) {
    targetPanels.children.forEach((panel: any) => {
      panel.visible = true;
    });
  }

  previewPoints = [];
  previewShadows = [];
  previewPanelsToRemove = [];
  lastTooltipContent = null;
  lastHiddenPanelIds = [];
  resetPreviewModuleCounts();
}

export function resetRubberBandPreviewState() {
  (debouncedShadowSync as any).cancel();
  previewPoints = [];
  previewShadows = [];
  previewPanelsToRemove = [];
  lastTooltipContent = null;
  lastHiddenPanelIds = [];
  outlineGraphic = null;
  panelsShadowContainer = null;
  resetPreviewModuleCounts();
}

function removeOutline(stage: PIXI.Container) {
  if (outlineGraphic) {
    if (stage && stage.children.includes(outlineGraphic)) {
      stage.removeChild(outlineGraphic);
    }
    if (!outlineGraphic.destroyed) {
      outlineGraphic.destroy();
    }
    outlineGraphic = null;
  } else if (stage) {
    const existing = stage.children.find((child: any) => child.id === 'rubberBand');
    if (existing) {
      stage.removeChild(existing);
    }
  }
}
